import { ReactElement, useMemo, useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import { USGA_SCORE_TYPES } from '../../types/UsgaPost';
import { usgaPostPreviewLines } from '../../utils/usgaPostPreview';

import type { UsgaScoreType } from '../../types/UsgaPost';
import type { UsgaPostBuild } from '../../utils/usgaScorePostUtils';

export interface UsgaPostChoices {
  scoreType: UsgaScoreType;
  adjustedGrossOverride: number | null;
  holesPlayedOverride: number | null;
  skipSeasonCheck: boolean;
}

interface EventScoreUsgaPostFormProps {
  /** Total-only scores ask for the adjusted score and holes played. */
  isTotalOnly: boolean;
  defaultAdjustedScore: number;
  defaultHolesPlayed: number;
  /** The score type chosen last time (Post again); none for a first post, so it's a deliberate choice. */
  initialScoreType: UsgaScoreType | null;
  submitLabel: string;
  /** What would be sent with these choices, or why it can't be. */
  preview: (choices: Omit<UsgaPostChoices, 'scoreType'>) => UsgaPostBuild;
  /** Bare-X holes whose value the server works out from the USGA's index when posting. */
  xHolesFromUsgaIndex: ReadonlySet<number>;
  onSubmit: (choices: UsgaPostChoices) => Promise<void>;
  onCancel: () => void;
}

const SCORE_TYPE_OPTIONS: { value: UsgaScoreType; label: string }[] = [
  { value: USGA_SCORE_TYPES.COMPETITION, label: 'Competition' },
  { value: USGA_SCORE_TYPES.HOME, label: 'Home' },
  { value: USGA_SCORE_TYPES.AWAY, label: 'Away' },
];

const toNumber = (value: string): number | null => {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : null;
};

/**
 * The inline panel for posting one score: Competition | Home | Away, the adjusted score and holes
 * played for a total-only score, and — on an off-season day — the staging-only "send anyway".
 */
export default function EventScoreUsgaPostForm(props: EventScoreUsgaPostFormProps): ReactElement {
  const { isTotalOnly, defaultAdjustedScore, defaultHolesPlayed, initialScoreType, submitLabel, preview, xHolesFromUsgaIndex, onSubmit, onCancel } = props;
  const [scoreType, setScoreType] = useState<UsgaScoreType | null>(initialScoreType);
  const [adjusted, setAdjusted] = useState(String(defaultAdjustedScore || ''));
  const [holesPlayed, setHolesPlayed] = useState(String(defaultHolesPlayed));
  const [sendAnyway, setSendAnyway] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const overrides = useMemo(() => ({
    adjustedGrossOverride: isTotalOnly ? toNumber(adjusted) : null,
    holesPlayedOverride: isTotalOnly ? toNumber(holesPlayed) : null,
  }), [isTotalOnly, adjusted, holesPlayed]);

  const seasonCheck = preview({ ...overrides, skipSeasonCheck: false });
  const isOffSeason = !seasonCheck.ok && seasonCheck.block.code === 'OUT_OF_SEASON';
  const build = isOffSeason && sendAnyway ? preview({ ...overrides, skipSeasonCheck: true }) : seasonCheck;
  const canSubmit = !!scoreType && build.ok && !isSubmitting;

  const handleSubmit = async () => {
    if (!scoreType) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit({ scoreType, ...overrides, skipSeasonCheck: isOffSeason && sendAnyway });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Couldn\'t send the request.');
      setIsSubmitting(false);
    }
  };

  return (
    <Stack spacing={1}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={scoreType}
        onChange={(_e, value: UsgaScoreType | null) => { if (value) setScoreType(value); }}
        aria-label="Score type"
      >
        {SCORE_TYPE_OPTIONS.map(({ value, label }) => <ToggleButton key={value} value={value}>{label}</ToggleButton>)}
      </ToggleButtonGroup>

      {isTotalOnly && (
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            label="Adjusted score"
            value={adjusted}
            onChange={(e) => setAdjusted(e.target.value.replace(/\D/g, ''))}
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
            helperText="Lower any hole above net double bogey"
            sx={{ width: '10em' }}
          />
          <TextField
            size="small"
            label="Holes played"
            value={holesPlayed}
            onChange={(e) => setHolesPlayed(e.target.value.replace(/\D/g, ''))}
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
            sx={{ width: '7em' }}
          />
        </Stack>
      )}

      {/* What will be sent with these choices — for a total, the adjusted score and holes played typed above. */}
      {isTotalOnly && build.ok && usgaPostPreviewLines(build.sent, xHolesFromUsgaIndex).map((line) => (
        <Typography key={line} variant="bodySmall" color="text.secondary">{line}</Typography>
      ))}
      {!seasonCheck.ok && (
        <Typography variant="bodySmall" color={isOffSeason ? 'warning.main' : 'error'}>{seasonCheck.block.message}</Typography>
      )}
      {isOffSeason && (
        <FormControlLabel
          control={<Checkbox size="small" checked={sendAnyway} onChange={(e) => setSendAnyway(e.target.checked)} />}
          label="Send anyway (staging test of the USGA's off-season response)"
        />
      )}
      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction="row" spacing={1}>
        <Button size="small" variant="contained" disabled={!canSubmit} onClick={handleSubmit}>
          {isSubmitting ? <CircularProgress size="1.25em" /> : submitLabel}
        </Button>
        <Button size="small" onClick={onCancel} disabled={isSubmitting}>Cancel</Button>
      </Stack>
    </Stack>
  );
}
